import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { ArrowRight, Check, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthShell } from "@/components/auth/AuthShell";
import { sanitizeText } from "@/lib/sanitize";

const rules = (pwd: string) => [
  { ok: pwd.length >= 8, label: "8+ characters" },
  { ok: /[A-Z]/.test(pwd), label: "Uppercase letter" },
  { ok: /[a-z]/.test(pwd), label: "Lowercase letter" },
  { ok: /[0-9]/.test(pwd), label: "Number" },
];

const Signup = () => {
  const navigate = useNavigate();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fullName.trim() || !email.trim() || !password) {
      toast.error("Please fill in all fields");
      return;
    }
    if (rules(password).some((r) => !r.ok)) {
      toast.error("Password doesn't meet requirements");
      return;
    }
    setLoading(true);
    try {
      const sanitized = sanitizeText(fullName, 100);
      const { error } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/dashboard`,
          data: { full_name: sanitized },
        },
      });
      if (error) {
        if (error.message.includes("already registered")) {
          throw new Error("This email is already registered. Sign in instead.");
        }
        throw error;
      }
      toast.success("Account created", { description: "Pick a plan to start your 7-day free trial." });
      // Card is required to start the trial (same as systeme.io). The welcome
      // email is sent after checkout succeeds, from the Pricing page.
      navigate("/pricing?start=trial");
    } catch (err: any) {
      toast.error("Sign up failed", { description: err.message });
    } finally {
      setLoading(false);
    }
  };

  const checks = rules(password);

  return (
    <>
      <Helmet>
        <title>Create an account | EchoCRM</title>
        <link rel="canonical" href="https://www.echocrm.app/signup" />
      </Helmet>

      <AuthShell
        eyebrow="Get started"
        title="Create your account."
        subtitle="Free to start. No credit card required."
        footer={
          <>
            Already have an account?{" "}
            <Link to="/login" className="text-foreground hover:text-primary">Sign in</Link>
          </>
        }
      >
        <form onSubmit={handleSignup} className="space-y-5">
          <div className="space-y-1.5">
            <Label htmlFor="fullName" className="text-[13px] font-medium">Full name</Label>
            <Input
              id="fullName"
              autoComplete="name"
              placeholder="Jane Cooper"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email" className="text-[13px] font-medium">Email</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password" className="text-[13px] font-medium">Password</Label>
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
            />
            {password && (
              <ul className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2">
                {checks.map((r) => (
                  <li key={r.label} className={`flex items-center gap-1.5 text-[12px] ${r.ok ? "text-foreground" : "text-muted-foreground"}`}>
                    {r.ok ? <Check className="h-3 w-3 text-primary" /> : <X className="h-3 w-3 opacity-50" />}
                    {r.label}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <Button type="submit" size="lg" className="w-full" disabled={loading}>
            {loading ? "Creating account…" : <>Create account <ArrowRight className="h-4 w-4" /></>}
          </Button>

          <p className="text-[12px] text-muted-foreground leading-[1.55]">
            By creating an account you agree to our{" "}
            <Link to="/terms" className="hover:text-foreground underline underline-offset-2">Terms</Link> and{" "}
            <Link to="/privacy" className="hover:text-foreground underline underline-offset-2">Privacy Policy</Link>.
          </p>
        </form>
      </AuthShell>
    </>
  );
};

export default Signup;